import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { 
  Upload, 
  FileText, 
  Download, 
  Trash2, 
  Save, 
  AlertCircle,
  Target,
  Users,
  Calendar,
  DollarSign,
  Shield,
  CheckCircle2,
  StickyNote,
  GitBranch,
  Info
} from 'lucide-react';


const ProjectSettingsInline = ({ project, onUpdate }) => {
  const [settings, setSettings] = useState({
    scope_of_work_text: '',
    project_objectives: '',
    key_deliverables: '',
    timeline_notes: '',
    stakeholders: '',
    budget_notes: '',
    risk_factors: '',
    success_criteria: '',
    additional_notes: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [scopeFiles, setScopeFiles] = useState([]);
  const [activeTab, setActiveTab] = useState('general');
  const [teamMembers, setTeamMembers] = useState([]);
  const [projectManager, setProjectManager] = useState('');
  const [projectStages, setProjectStages] = useState([]);
  const [projectRules, setProjectRules] = useState([]);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleFormData, setRuleFormData] = useState({
    stage_id: '',
    assignee_type: 'project_manager',
    assignee_id: ''
  });
  const [projectCategory, setProjectCategory] = useState(project?.category || '');
  const [taskCategories, setTaskCategories] = useState([]);

  useEffect(() => {
    if (project?.id) {
      loadProjectSettings();
      loadTeamMembers();
      loadProjectStages();
      loadProjectRules();
      loadTaskCategories();
      setProjectManager(project?.project_manager_id || '');
      setProjectCategory(project?.category || '');
    }
  }, [project?.id]);

  const loadTaskCategories = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/task-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && Array.isArray(response.data)) {
        setTaskCategories(response.data);
      }
    } catch (error) {
      console.error('Failed to load task categories:', error);
    }
  };

  const handleCategoryChange = async (newCategory) => {
    try {
      const token = localStorage.getItem('access_token');
      await api.put(`/projects/${project.id}`, 
        { category: newCategory || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProjectCategory(newCategory);
      if (onUpdate) {
        onUpdate({ ...project, category: newCategory });
      }
    } catch (error) {
      console.error('Failed to update project category:', error);
    }
  };

  const loadProjectSettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/projects/${project.id}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSettings({
        project_name: response.data.project_name || '',
        project_description: response.data.project_description || '',
        scope_of_work_text: response.data.scope_of_work_text || '',
        project_objectives: response.data.project_objectives || '',
        key_deliverables: response.data.key_deliverables || '',
        timeline_notes: response.data.timeline_notes || '',
        stakeholders: response.data.stakeholders || '',
        budget_notes: response.data.budget_notes || '',
        risk_factors: response.data.risk_factors || '',
        success_criteria: response.data.success_criteria || '',
        additional_notes: response.data.additional_notes || ''
      });
      
      setScopeFiles(response.data.scope_of_work_files || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load project settings:', error);
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const members = response.data.members || [];
      console.log('Loaded team members:', members.length, members);
      setTeamMembers(members);
    } catch (error) {
      console.error('Failed to load team members:', error);
    }
  };

  const loadProjectStages = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // First try to get project-specific stages
      const projectResponse = await api.get(`/projects/${project.id}/stages-full`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let stages = projectResponse.data.stages || [];
      
      // If no custom stages, load default team stages
      if (!stages || stages.length === 0) {
        const defaultResponse = await api.get(`/default-project-stages`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        stages = defaultResponse.data || [];
      }
      
      setProjectStages(stages);
    } catch (error) {
      console.error('Failed to load project stages:', error);
    }
  };

  const loadProjectRules = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/projects/${project.id}/assignment-rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectRules(response.data || []);
    } catch (error) {
      console.error('Failed to load project assignment rules:', error);
    }
  };

  const handleCreateRule = () => {
    setRuleFormData({
      stage_id: '',
      assignee_type: 'project_manager',
      assignee_id: ''
    });
    setEditingRule(null);
    setShowRuleModal(true);
  };

  const handleEditRule = (rule) => {
    setRuleFormData({
      stage_id: rule.stage_id,
      assignee_type: rule.assignee_type,
      assignee_id: rule.assignee_id || ''
    });
    setEditingRule(rule);
    setShowRuleModal(true);
  };

  const handleSaveRule = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      if (editingRule) {
        // Update existing rule
        await api.put(`/projects/${project.id}/assignment-rules/${editingRule.id}`, ruleFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Rule updated successfully!');
      } else {
        // Create new rule
        await api.post(`/projects/${project.id}/assignment-rules`, ruleFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Rule created successfully!');
      }
      
      setShowRuleModal(false);
      loadProjectRules();
    } catch (error) {
      console.error('Failed to save rule:', error);
      alert('Failed to save rule. Please try again.');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;

    try {
      const token = localStorage.getItem('access_token');
      await api.delete(`/projects/${project.id}/assignment-rules/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Rule deleted successfully!');
      loadProjectRules();
    } catch (error) {
      console.error('Failed to delete rule:', error);
      alert('Failed to delete rule. Please try again.');
    }
  };

  const handleSaveProjectManager = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await api.put(`/projects/${project.id}`, {
        ...project,
        project_manager_id: projectManager || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (onUpdate) {
        onUpdate();
      }
      
      alert('Project manager updated successfully!');
    } catch (error) {
      console.error('Failed to update project manager:', error);
      alert('Failed to update project manager. Please try again.');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      await api.post(`/projects/${project.id}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (onUpdate) {
        onUpdate();
      }
      
      alert('Project settings saved successfully!');
    } catch (error) {
      console.error('Failed to save project settings:', error);
      alert('Failed to save project settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('access_token');
      const response = await api.post(
        `/projects/${project.id}/settings/upload-scope`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Add the new file to the list with the proper structure
      const newFile = {
        original_filename: response.data.filename,
        file_url: response.data.file_url,
        gcs_path: response.data.gcs_path,
        file_size: response.data.file_size
      };
      setScopeFiles([...scopeFiles, newFile]);
      alert('File uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteFile = async (fileUrl) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      const token = localStorage.getItem('access_token');
      await api.delete(`/projects/${project.id}/settings/scope-files`, {
        params: { file_url: fileUrl },
        headers: { Authorization: `Bearer ${token}` }
      });

      setScopeFiles(scopeFiles.filter(f => f.file_url !== fileUrl));
      alert('File deleted successfully!');
    } catch (error) {
      console.error('Failed to delete file:', error);
      alert('Failed to delete file. Please try again.');
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const tabs = [
    { id: 'general', label: 'General', icon: Info },
    { id: 'scope', label: 'Scope of Work', icon: FileText },
    { id: 'objectives', label: 'Objectives & Deliverables', icon: Target },
    { id: 'timeline', label: 'Timeline & Stakeholders', icon: Calendar },
    { id: 'budget', label: 'Budget & Risk', icon: DollarSign },
    { id: 'assignment', label: 'Assignment', icon: Users },
    { id: 'additional', label: 'Additional Notes', icon: StickyNote }
  ];

  return (
    <div className="bg-white">
      {/* Tab Navigation */}
      <div className="flex space-x-4 px-6 py-4 border-b border-gray-200 overflow-x-auto">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-purple-100 text-purple-700 border border-purple-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="text-sm font-medium">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Loading project settings...</div>
          </div>
        ) : (
          <>
            {/* General Tab */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                  <Info className="w-5 h-5 text-purple-600" />
                  <span>General Settings</span>
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Category
                    </label>
                    <select
                      value={projectCategory}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">No Category</option>
                      {taskCategories.map(category => (
                        <option key={category.id} value={category.name}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Categorize this project for better organization and workflow automation
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Scope of Work Tab */}
            {activeTab === 'scope' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                    <FileText className="w-5 h-5 text-purple-600" />
                    <span>Scope of Work</span>
                  </h3>
                  <textarea
                    value={settings.scope_of_work_text}
                    onChange={(e) => handleInputChange('scope_of_work_text', e.target.value)}
                    placeholder="Describe the project scope, requirements, and boundaries..."
                    rows={8}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* File Attachments */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Scope Documents</label>
                  <div className="space-y-3">
                    {scopeFiles && scopeFiles.length > 0 && scopeFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <FileText className="w-5 h-5 text-gray-400" />
                          <span className="text-sm text-gray-700">{file.original_filename || file.filename || 'Unnamed file'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <a
                            href={file.file_url || file.gcs_path}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                          <button
                            onClick={() => handleDeleteFile(file.file_url || file.gcs_path)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <label className="mt-3 flex items-center justify-center w-full p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-500 transition-colors">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Upload className="w-5 h-5" />
                      <span className="text-sm">{uploadingFile ? 'Uploading...' : 'Upload Scope Document'}</span>
                    </div>
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      disabled={uploadingFile}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            )}

            {/* Objectives Tab */}
            {activeTab === 'objectives' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                  <Target className="w-5 h-5 text-purple-600" />
                  <span>Project Objectives & Deliverables</span>
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Objectives</label>
                  <textarea
                    value={settings.project_objectives}
                    onChange={(e) => handleInputChange('project_objectives', e.target.value)}
                    placeholder="What are the main objectives and goals of this project?"
                    rows={5}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Key Deliverables</label>
                  <textarea
                    value={settings.key_deliverables}
                    onChange={(e) => handleInputChange('key_deliverables', e.target.value)}
                    placeholder="List the key deliverables and milestones..."
                    rows={5}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Success Criteria</label>
                  <textarea
                    value={settings.success_criteria}
                    onChange={(e) => handleInputChange('success_criteria', e.target.value)}
                    placeholder="How will success be measured for this project?"
                    rows={5}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* Timeline Tab */}
            {activeTab === 'timeline' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  <span>Timeline & Stakeholders</span>
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Timeline Notes</label>
                  <textarea
                    value={settings.timeline_notes}
                    onChange={(e) => handleInputChange('timeline_notes', e.target.value)}
                    placeholder="Important dates, deadlines, and timeline considerations..."
                    rows={5}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stakeholders</label>
                  <textarea
                    value={settings.stakeholders}
                    onChange={(e) => handleInputChange('stakeholders', e.target.value)}
                    placeholder="Key stakeholders, decision makers, and their roles..."
                    rows={5}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* Budget Tab */}
            {activeTab === 'budget' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                  <span>Budget & Risk Management</span>
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Budget Notes</label>
                  <textarea
                    value={settings.budget_notes}
                    onChange={(e) => handleInputChange('budget_notes', e.target.value)}
                    placeholder="Budget constraints, cost estimates, and financial considerations..."
                    rows={5}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Risk Factors</label>
                  <textarea
                    value={settings.risk_factors}
                    onChange={(e) => handleInputChange('risk_factors', e.target.value)}
                    placeholder="Potential risks, dependencies, and mitigation strategies..."
                    rows={5}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}


            {/* Combined Assignment Tab */}
            {activeTab === 'assignment' && (
              <div className="space-y-8">
                {/* Header */}
                <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  <span>Team & Assignment Configuration</span>
                </h3>

                {/* Section 1: Project Manager */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <Shield className="w-4 h-4 text-blue-600" />
                    <span>Project Manager</span>
                  </h4>
                  
                  <p className="text-sm text-gray-600 mb-4">
                    Assign a project manager who will oversee this project. The PM can be automatically assigned to tasks based on auto-assignment rules.
                  </p>

                  <div className="flex items-end space-x-3">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Project Manager
                      </label>
                      <select
                        value={projectManager}
                        onChange={(e) => setProjectManager(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                      >
                        <option value="">No Project Manager</option>
                        {teamMembers.map(member => (
                          <option key={member.user?.id || member.id} value={member.user?.id || member.id}>
                            {member.user?.name || member.name} ({member.user?.email || member.email})
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={handleSaveProjectManager}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
                    >
                      <Save className="w-4 h-4" />
                      <span>Save PM</span>
                    </button>
                  </div>

                  {projectManager && (
                    <div className="mt-4 p-3 bg-white rounded-lg border border-blue-300">
                      <p className="text-sm text-blue-800">
                        <CheckCircle2 className="w-4 h-4 inline mr-2" />
                        This project manager will be used when auto-assignment rules target &ldquo;Project Manager&rdquo;
                      </p>
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200"></div>

                {/* Section 2: Stage-Based Assignment Rules */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-md font-semibold text-gray-900 flex items-center space-x-2">
                        <GitBranch className="w-4 h-4 text-purple-600" />
                        <span>Stage-Based Assignment Rules</span>
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Automatically assign tasks when they enter specific stages
                      </p>
                    </div>
                    <button
                      onClick={handleCreateRule}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
                    >
                      <span>+ Create Rule</span>
                    </button>
                  </div>

                {/* Assignment Rules List */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Active Rules</h4>
                  
                  {projectRules && projectRules.length > 0 ? (
                    <div className="space-y-3">
                      {projectRules.map((rule) => {
                        const stage = projectStages.find(s => s.id === rule.stage_id);
                        return (
                          <div 
                            key={rule.id}
                            className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                                  {stage?.name || 'Unknown Stage'}
                                </span>
                                <span className="text-gray-400">→</span>
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                                  {rule.assignee_type === 'project_manager' ? 'Project Manager' :
                                   rule.assignee_type === 'account_manager' ? 'Account Manager' :
                                   rule.assignee_type === 'specific_user' ? 'Specific User' : rule.assignee_type}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600">
                                When task enters &ldquo;{stage?.name}&rdquo; stage, assign to {
                                  rule.assignee_type === 'project_manager' ? 'the project manager' :
                                  rule.assignee_type === 'account_manager' ? 'the account manager' :
                                  'a specific user'
                                }
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEditRule(rule)}
                                className="px-3 py-1 text-sm text-purple-600 hover:bg-purple-50 rounded transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteRule(rule.id)}
                                className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                      <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-600 mb-2">No assignment rules configured</p>
                      <p className="text-sm text-gray-500 mb-4">
                        Create rules to automatically assign tasks when they move to specific stages
                      </p>
                      <button
                        onClick={handleCreateRule}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        Create Your First Rule
                      </button>
                    </div>
                  )}
                </div>

                {/* Rule Modal */}
                {showRuleModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                      <div className="p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          {editingRule ? 'Edit Assignment Rule' : 'Create Assignment Rule'}
                        </h3>

                        <div className="space-y-4">
                          {/* Stage Selection */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              When task enters stage *
                            </label>
                            <select
                              value={ruleFormData.stage_id}
                              onChange={(e) => setRuleFormData({ ...ruleFormData, stage_id: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            >
                              <option value="">Select a stage...</option>
                              {projectStages && projectStages.length > 0 ? (
                                projectStages.map(stage => (
                                  <option key={stage.id} value={stage.id}>{stage.name}</option>
                                ))
                              ) : (
                                <option disabled>No stages available</option>
                              )}
                            </select>
                            {projectStages && projectStages.length === 0 && (
                              <p className="text-sm text-red-600 mt-1">No stages found. Please configure project stages first.</p>
                            )}
                          </div>

                          {/* Assignee Type */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Assign to *
                            </label>
                            <select
                              value={ruleFormData.assignee_type}
                              onChange={(e) => setRuleFormData({ ...ruleFormData, assignee_type: e.target.value, assignee_id: '' })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            >
                              <option value="project_manager">Project Manager</option>
                              <option value="account_manager">Account Manager</option>
                              <option value="specific_user">Specific User</option>
                            </select>
                          </div>

                          {/* Specific User Selection */}
                          {ruleFormData.assignee_type === 'specific_user' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select User *
                              </label>
                              <select
                                value={ruleFormData.assignee_id}
                                onChange={(e) => setRuleFormData({ ...ruleFormData, assignee_id: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              >
                                <option value="">Select a user...</option>
                                {teamMembers && teamMembers.length > 0 ? (
                                  teamMembers.map(member => (
                                    <option key={member.user?.id || member.id} value={member.user?.id || member.id}>
                                      {member.user?.name || member.name} ({member.user?.email || member.email})
                                    </option>
                                  ))
                                ) : (
                                  <option disabled>Loading users...</option>
                                )}
                              </select>
                              {teamMembers && teamMembers.length === 0 && (
                                <p className="text-sm text-red-600 mt-1">No team members found.</p>
                              )}
                            </div>
                          )}

                          {/* Info about manager types */}
                          {(ruleFormData.assignee_type === 'project_manager' || ruleFormData.assignee_type === 'account_manager') && (
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <p className="text-sm text-blue-800">
                                {ruleFormData.assignee_type === 'project_manager' 
                                  ? 'Will use the Project Manager set in the Team & Assignment tab'
                                  : 'Will use the Account Manager set on the client'}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Modal Actions */}
                        <div className="flex justify-end space-x-3 mt-6">
                          <button
                            onClick={() => setShowRuleModal(false)}
                            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveRule}
                            disabled={!ruleFormData.stage_id || (ruleFormData.assignee_type === 'specific_user' && !ruleFormData.assignee_id)}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {editingRule ? 'Update Rule' : 'Create Rule'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                </div>
              </div>
            )}

            {/* Additional Notes Tab */}
            {activeTab === 'additional' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                  <StickyNote className="w-5 h-5 text-purple-600" />
                  <span>Additional Notes</span>
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                  <textarea
                    value={settings.additional_notes}
                    onChange={(e) => handleInputChange('additional_notes', e.target.value)}
                    placeholder="Any additional notes, requirements, or considerations for this project..."
                    rows={10}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* Save Button - Fixed at bottom */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'Saving...' : 'Save Settings'}</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectSettingsInline;
